// #tag: ADMIN CONSOLE LAYOUT SHELL
// #purpose: Implements full-screen admin control center, collapsible sidebar, top bar, RBAC navigation filtering, and theme switching.
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Bell,
  ChevronDown,
  ChevronRight,
  Menu,
  Moon,
  Search,
  Sparkles,
  Sun,
  X,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthProvider';
import { useAppearance } from '../../context/AppearanceProvider';
import { fetchAdminMe } from '../../api/admin';
import { ADMIN_NAV, filterAdminNav } from '../../config/adminNavigation';
import { resolveIcon } from '../../lib/iconMap';
import type { AdminNavItem } from '../../types/admin';

function SidebarLink({ item, collapsed }: { item: AdminNavItem; collapsed: boolean }) {
  const Icon = resolveIcon(item.icon);
  if (!item.route) return null;
  return (
    <NavLink
      to={item.route}
      end={item.route === '/admin'}
      className={({ isActive }) => `wfx-admin-nav-link${isActive ? ' is-active' : ''}`}
      title={collapsed ? item.label : undefined}
    >
      <Icon size={18} aria-hidden="true" />
      {!collapsed && <span>{item.label}</span>}
    </NavLink>
  );
}

function SidebarGroup({ item, collapsed }: { item: AdminNavItem; collapsed: boolean }) {
  const [open, setOpen] = useState(true);
  const Icon = resolveIcon(item.icon);
  const location = useLocation();
  const isChildActive = item.children?.some((child) => child.route && location.pathname.startsWith(child.route));

  if (!item.children?.length) {
    return <SidebarLink item={item} collapsed={collapsed} />;
  }

  return (
    <div className={`wfx-admin-nav-group${isChildActive ? ' is-active' : ''}`}>
      <button
        type="button"
        className="wfx-admin-nav-group-btn"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Icon size={18} aria-hidden="true" />
        {!collapsed && (
          <>
            <span>{item.label}</span>
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </>
        )}
      </button>
      {!collapsed && open && (
        <div className="wfx-admin-nav-children">
          {item.children.map((child) => (
            <SidebarLink key={child.id} item={child} collapsed={false} />
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminShell() {
  const { user, logout } = useAuth();
  const { settings, setSettings } = useAppearance();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const { data: adminMe } = useQuery({
    queryKey: ['admin-me'],
    queryFn: fetchAdminMe,
    staleTime: 60_000,
  });

  const navItems = useMemo(
    () => filterAdminNav(
      ADMIN_NAV,
      adminMe?.permissions ?? [],
      adminMe?.isSuperAdmin ?? false,
    ),
    [adminMe],
  );

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const breadcrumb = location.pathname.replace('/admin', 'Admin').replace(/\//g, ' / ') || 'Admin / Dashboard';

  return (
    <div className={`wfx-admin-shell${collapsed ? ' is-collapsed' : ''}${adminMe?.isSuperAdmin ? ' is-super-admin' : ''}`}>
      <aside className={`wfx-admin-sidebar glass-surface${sidebarOpen ? ' is-open' : ''}`}>
        <div className="wfx-admin-brand">
          <strong>WebFoxx Admin</strong>
          <span className={adminMe?.isSuperAdmin ? 'wfx-admin-role-super' : ''}>
            {adminMe?.isSuperAdmin ? 'Super Admin' : user?.role?.replace('_', ' ')}
          </span>
        </div>
        <nav className="wfx-admin-nav" aria-label="Admin navigation">
          {navItems.map((item) => (
            <SidebarGroup key={item.id} item={item} collapsed={collapsed} />
          ))}
        </nav>
      </aside>

      {sidebarOpen && (
        <button type="button" className="wfx-admin-backdrop" aria-label="Close menu" onClick={() => setSidebarOpen(false)} />
      )}

      <div className="wfx-admin-main">
        <header className="wfx-admin-header glass-surface">
          <div className="wfx-admin-header-left">
            <button type="button" className="ghost-icon-button wfx-admin-menu-btn" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
              <Menu size={20} />
            </button>
            <button type="button" className="ghost-icon-button wfx-admin-collapse-btn" onClick={() => setCollapsed((v) => !v)} aria-label="Toggle sidebar">
              {collapsed ? <ChevronRight size={18} /> : <Menu size={18} />}
            </button>
            <div>
              <p className="wfx-admin-breadcrumb">{breadcrumb}</p>
              <h1 className="wfx-admin-page-title">Command Center</h1>
            </div>
          </div>

          <div className="wfx-admin-search">
            <Search size={16} aria-hidden="true" />
            <input type="search" placeholder="Search users, results, pages, jobs…" aria-label="Global admin search" />
          </div>

          <div className="wfx-admin-header-actions">
            <button type="button" className="ghost-icon-button" aria-label="Notifications"><Bell size={18} /></button>
            <button type="button" className="ghost-icon-button" aria-label="AI Assistant"><Sparkles size={18} /></button>
            <button
              type="button"
              className="ghost-icon-button"
              aria-label="Toggle theme"
              onClick={() => setSettings({
                theme: settings.theme === 'dark' ? 'light' : 'dark',
              })}
            >
              {settings.theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button type="button" className="wfx-admin-profile-chip" onClick={logout}>
              <span>{user?.name?.slice(0, 1) ?? 'A'}</span>
              <span>{user?.name}</span>
            </button>
            <button type="button" className="ghost-icon-button wfx-admin-close-mobile" onClick={() => setSidebarOpen(false)} aria-label="Close">
              <X size={18} />
            </button>
          </div>
        </header>

        <main className="wfx-admin-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
